/* eslint-disable */
import { useState, useCallback, useEffect, useRef } from 'react';
import { MultichoixBulkInitializeService } from '../services/multichoixBulkInitializeService';

/**
 * Initial enriched data structure for treatment search
 */
const initialEnrichedData = {
  cultures: [],
  culturalPractices: [],
  treatmentTypes: [],
  applicationTypes: [],
  interventionPeriods: [],
};

/**
 * Maps URL field names to API field names for bulk initialization
 */
const FIELD_TO_API_MAPPING = {
  'treatment_cultures.culture': 'culture_ids',
  'treatment_cultures.cultural_practices': 'cultural_practice_ids',
  'treatment_type': 'treatment_type_ids',
  'application_type': 'application_type_ids',
  'intervention_period': 'intervention_period_ids',
};

/**
 * Maps API response keys to enriched data keys
 */
const API_TO_ENRICHED_MAPPING = {
  cultures: 'cultures',
  cultural_practices: 'culturalPractices',
  treatment_types: 'treatmentTypes',
  application_types: 'applicationTypes',
  intervention_periods: 'interventionPeriods',
};

/**
 * Maps URL field names to enriched data keys
 */
const FIELD_TO_ENRICHED_MAPPING = {
  'treatment_cultures.culture': 'cultures',
  'treatment_cultures.cultural_practices': 'culturalPractices',
  'treatment_type': 'treatmentTypes',
  'application_type': 'applicationTypes',
  'intervention_period': 'interventionPeriods',
};

/**
 * Check if URL state has any data that needs label fetching
 */
const hasUrlDataToFetch = (state) => {
  return Object.keys(FIELD_TO_API_MAPPING).some((fieldName) => {
    const values = state?.[fieldName];
    return values && Array.isArray(values) && values.length > 0;
  });
};

/**
 * Hook for managing enriched data with labels for treatment search
 * Handles bulk fetching for URL state restoration (cold start)
 *
 * @param {Object} urlState - Current URL state with field values
 * @returns {Object} Enriched data state and helper functions
 */
export function useTreatmentEnrichedData(urlState) {
  const [enrichedData, setEnrichedData] = useState(initialEnrichedData);
  // Initialize isLoadingLabels to true if URL has data to fetch (prevents race condition)
  const [isLoadingLabels, setIsLoadingLabels] = useState(() => hasUrlDataToFetch(urlState));
  const hasInitializedRef = useRef(false);

  /**
   * Transforms an API response item to a label object
   * @param {Object} item - API response item
   * @returns {Object} Label object with value, id, and label
   */
  const transformToLabelObject = useCallback((item) => {
    const label =
      item.french_name ||
      item.english_name ||
      item.latin_name ||
      item.name ||
      item.display_name ||
      item.id;
    return {
      value: item.id,
      id: item.id,
      label,
    };
  }, []);

  /**
   * Bulk fetches labels for URL state restoration
   * @param {Object} state - URL state with field values
   * @returns {Promise<Object>} Enriched data with labels
   */
  const fetchLabelsForURLState = useCallback(
    async (state) => {
      const result = { ...initialEnrichedData };

      // Build bulk API request from URL state
      const bulkRequest = {};
      let hasAnyData = false;

      Object.entries(FIELD_TO_API_MAPPING).forEach(([fieldName, apiFieldName]) => {
        const values = state[fieldName];
        if (values && Array.isArray(values) && values.length > 0) {
          bulkRequest[apiFieldName] = values;
          hasAnyData = true;
        }
      });

      if (!hasAnyData) {
        return result;
      }

      try {
        const bulkData = await MultichoixBulkInitializeService.bulkInitialize(bulkRequest);

        // Transform API response to enriched data
        Object.entries(API_TO_ENRICHED_MAPPING).forEach(([apiKey, enrichedKey]) => {
          if (bulkData[apiKey] && Array.isArray(bulkData[apiKey])) {
            result[enrichedKey] = bulkData[apiKey].map(transformToLabelObject);
          }
        });

        return result;
      } catch (error) {
        console.error('[useTreatmentEnrichedData] Failed to fetch labels:', error);
        return result;
      }
    },
    [transformToLabelObject]
  );

  // Fetch labels ONLY on cold start (initial page load with IDs in URL)
  // Interactive filter selections handle labels via updateFilterWithLabels/setFilterLabel
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    const hasUrlData = hasUrlDataToFetch(urlState);

    if (!hasUrlData) {
      setIsLoadingLabels(false);
      hasInitializedRef.current = true;
      return;
    }

    hasInitializedRef.current = true;
    setIsLoadingLabels(true);

    fetchLabelsForURLState(urlState)
      .then((newEnrichedData) => {
        setEnrichedData(newEnrichedData);
        setIsLoadingLabels(false);
      })
      .catch((error) => {
        console.error('[useTreatmentEnrichedData] Error in useEffect:', error);
        setIsLoadingLabels(false);
      });
  }, [urlState, fetchLabelsForURLState]);

  /**
   * Reset enriched data to initial state
   */
  const resetEnrichedData = useCallback(() => {
    setEnrichedData(initialEnrichedData);
    hasInitializedRef.current = false;
  }, []);

  /**
   * Update a specific field in enriched data
   * @param {string} field - URL field name (e.g., 'treatment_cultures.culture')
   * @param {Array} items - Array of label objects
   */
  const updateEnrichedField = useCallback((field, items) => {
    const enrichedKey = FIELD_TO_ENRICHED_MAPPING[field];
    if (enrichedKey) {
      setEnrichedData((prev) => ({
        ...prev,
        [enrichedKey]: items,
      }));
    }
  }, []);

  /**
   * Get labels for a specific field
   * @param {string} field - URL field name
   * @returns {Array} Array of label objects for the field
   */
  const getLabelsForField = useCallback(
    (field) => {
      const enrichedKey = FIELD_TO_ENRICHED_MAPPING[field];
      return enrichedKey ? enrichedData[enrichedKey] : [];
    },
    [enrichedData]
  );

  /**
   * Build selection labels in the format expected by treatment steps
   * Returns an object mapping field names to arrays of label objects
   * @returns {Object} Selection labels keyed by field name
   */
  const buildSelectionLabels = useCallback(() => {
    const selectionLabels = {};

    Object.entries(FIELD_TO_ENRICHED_MAPPING).forEach(([fieldName, enrichedKey]) => {
      const items = enrichedData[enrichedKey];
      if (items && items.length > 0) {
        selectionLabels[fieldName] = items;
      }
    });

    return selectionLabels;
  }, [enrichedData]);

  return {
    enrichedData,
    isLoadingLabels,
    resetEnrichedData,
    updateEnrichedField,
    getLabelsForField,
    buildSelectionLabels,
  };
}

export default useTreatmentEnrichedData;
